import React, { useEffect, useState, useMemo } from "react";
import DataGrid, { Column } from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import scorecardData from "../../data/scorecardData";
import "./ScorecardGrid.css";
import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";

interface ScorecardGridProps {
  p_type?: string;
}

interface ScorecardRow {
  name: string;
  isBold?: boolean;
  rowNo?: number;
  [key: string]: any;
}

interface ScorecardResponse {
  fys: number[];
  data: ScorecardRow[];
}

interface FyColsProps {
  path: string;
  caption: string;
  showTotal?: boolean;
  [key: string]: any;
}

const ScorecardGrid: React.FC<ScorecardGridProps> = ({ p_type = 'undefined' }) => {
  const [cobDate, setCobDate, cobLoading] = useCobDate("/pnl_latest_cob");
  const [marketData, setMarketData] = useState<ScorecardRow[]>([]);
  const [years, setYears] = useState<number[]>([]);

    useEffect(() => {
       if (!cobDate) return;

      const formattedSelectedDate = cobDate.toISOString().split("T")[0];
    const url = `${baseUrl}/pnl/pnl_scorecard?cob_date=${formattedSelectedDate}&p_type=${p_type}`;
    fetch(url)
      .then((res) => res.json())
      .then(({ fys, data }: ScorecardResponse) => {
        setYears(fys);
        const data_indexed = data.map((item, index) => ({
        //rowNo: index + 1,   // 1, 2, 3, ...
        ...item
      }));
        setMarketData(data_indexed);
        //console.log('scorecard data received ',fys,data);
      })
      .catch((err) => console.error("Failed to load market corr data", err));
  }, [cobDate, baseUrl]);
 const lo = Math.min(...years);
  const hi = Math.max(...years);
  const totalKey = `performanceScore.total_${lo}_${hi}`;

  const FyCols: React.FC<FyColsProps> = ({ path, caption, showTotal = false }) => (
             // this function returns dynamic grid fields like
       // <Column dataField="performanceScore.FY2026" caption="FY 2026" cssClass="center-header" />
       //<Column dataField="performanceScore.FY2025" caption="FY 2025" cssClass="center-header" />

    <Column caption={caption} cssClass="center-header">
      {showTotal && (
        <Column dataField={totalKey} caption={`${lo}-${hi}`} cssClass="center-header" width={90} minWidth={90} sortOrder="desc"
  sortIndex={0} cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }}/>
      )}
      {years.map((y) => (
        <Column
          key={`${path}-FY${y}`}
          dataField={`${path}.FY${y}`}
          caption={`FY ${y}`}
cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }}
          cssClass="center-header"
        />
      ))}
    </Column>
  );

function onContentReady(e: any) {
  const container = e.component
    .element()
    .querySelector(".dx-datagrid-rowsview .dx-scrollable-container") as HTMLElement | null;
  if (!container) return;

  // avoid double-binding if contentReady fires again
  if ((container as any).__dragBound) return;
  (container as any).__dragBound = true;

  let dragging = false;
  let startX = 0, startY = 0;

  const isInteractive = (el: Element) =>
    el.closest('input, textarea, select, button, a, [role="button"], .dx-editor-cell');

  container.style.cursor = "grab";
  container.style.touchAction = "none"; // allow custom panning

  container.addEventListener("pointerdown", (ev: PointerEvent) => {
    if (ev.button !== 0) return;               // left-click only
    if (isInteractive(ev.target as Element)) return;      // don't hijack editors/links
    dragging = true;
    startX = ev.clientX;
    startY = ev.clientY;
    container.setPointerCapture(ev.pointerId);
    container.style.cursor = "grabbing";
    ev.preventDefault();
  });

  container.addEventListener("pointermove", (ev: PointerEvent) => {
    if (!dragging) return;
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;

    // DevExtreme Scrollable API
    const sc = (e.component as any).getScrollable?.();
    if (sc) sc.scrollBy({ left: -dx, top: -dy });

    ev.preventDefault();
  });

  const endDrag = (ev: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    try { container.releasePointerCapture(ev.pointerId); } catch {}
    container.style.cursor = "grab";
  };
  container.addEventListener("pointerup", endDrag);
  container.addEventListener("pointercancel", endDrag);
  container.addEventListener("pointerleave", endDrag as EventListener);
}
const perfCols = ['fy2021', 'fy2022', 'fy2023', 'fy2024', 'fy2025', 'fy2026'];


const onCellPrepared = (e: any) => {
  const el = e.cellElement?.nodeType ? e.cellElement : e.cellElement?.get?.(0);
  if (!el) return;

  // Band header cell
  if (e.rowType === 'header' && e.column?.caption === 'Performance score') {
    el.classList.add('perf-band');
    return;
  }
   if (e.rowType === "data" && e.column.dataField === "rowNo") {
          const grid = e.component;
          const pageIndex = grid.pageIndex();
          const pageSize = grid.pageSize();
          const visibleIndex = e.rowIndex; // index on the current page

          // 1-based continuous numbering across pages
          e.cellElement.textContent = pageIndex * pageSize + visibleIndex + 1;
        }

  const df = e.column?.dataField;
  if (!df || !perfCols.includes(df)) return;

  // Inside the block
  el.classList.add('perf-cell');
  if (df === perfCols[0]) el.classList.add('perf-left');
  if (df === perfCols[perfCols.length - 1]) el.classList.add('perf-right');

  // Add bottom edge on the last visible data row (optional)
  if (e.rowType === 'data') {
    const rows = e.component.getVisibleRows();
    if (e.rowIndex === rows.length - 1) el.classList.add('perf-bottom');
  }
};


  return (
    <div className="league-container">
      {/* CoB Date Picker */}
      <div className="cob-calendar">
        <label>CoB:</label>
        <DateBox
          value={cobDate}
          onValueChanged={(e: any) => setCobDate(e.value)}
          displayFormat="dd-MMM-yyyy"
          width={160}
        />
      </div>

      {/* Data Grid */}
       <DataGrid
            elementAttr={{ class: 'tight-grid' }}
             onCellPrepared={onCellPrepared}

      dataSource={marketData}
      keyExpr="name"
      showBorders
      columnAutoWidth
      wordWrapEnabled
       onContentReady={onContentReady}
      // @ts-ignore
      rowClassName={(row: any) => (row.data?.isBold ? "bold-row" : "")}
    >

          <Column
    dataField="rowNo"
    caption=""
    width={50}
    allowSorting={false}
    allowFiltering={false}
    allowHeaderFiltering={false}
    allowEditing={false}
    fixed={true}
    fixedPosition="left"
    cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }}
  />
          <Column dataField="name" caption="Name" fixed={true}    fixedPosition="left" cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }} />
      <FyCols path="performanceScore" caption="Performance score" showTotal={true}  cssClass="wrap-header" cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }}  />
      <FyCols path="plMultiple"       caption="P&L as multiple of market risk capital" cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }}/>
      <FyCols path="sortinoRatio"     caption="Sortino Ratio" cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }} />
      <FyCols path="ytdPL"            caption="YTD P&L (€'000s)" cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }} />
      <FyCols path="marketRiskCapital" caption="Market Risk Capital (€'000s)" cellRender={({ value }: { value: any }) => {
    const n = Number(value);
    return Number.isNaN(n) ? value : n.toLocaleString();
  }} />
    </DataGrid>
    </div>
  );
};

export default ScorecardGrid;
